import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideStore } from '@ngxs/store';
import { TranslateService, provideTranslateService, provideTranslateLoader } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService
} from '@azure/msal-angular';
import { provideRouter, Router } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { catchError, map, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { UnauthorizedInterceptor } from './auth/services/unauthorized-interceptor.service';
import { provideTheme, SnackbarService } from '@siemens/component-lib';
import { UserManagementState } from './user-management/state/user-management.state';
import { AccountingUnitState } from './shared/state/accounting-unit/accounting-unit.state';
import { DataUploadState } from './shared/state/data-upload/data-upload.state';
import { TranslationLoader } from './core/translation-loader.service';
import { FleetManagementState } from './fleet-management/state/fleet-management.state';
import { LarsExportState } from './lars-export/state/lars-export.state';
import { QuarterlyExportState } from './admin/quarterly-export/state/quarterly-export.state';

const msalInstanceFactory = () => new PublicClientApplication(environment.msalConfig);

const msalInterceptorConfigFactory = (): MsalInterceptorConfiguration => {
    const protectedResourceMap = new Map<string, string[]>();
    for (const item of environment.apiConfig) {
        const uri = item.uri.startsWith('https://api.') ? item.uri + '/*' : item.uri;
        protectedResourceMap.set(uri, item.scopes);
    }
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
};

// TODO: Add the index of your API configuration that requires authentication here. 0 is currently the graph API.
const msalGuardConfigFactory = (): MsalGuardConfiguration => ({
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: [...environment.apiConfig[0].scopes] },
    loginFailedRoute: '/unauthorized'
});

function initializeMsal(msalService: MsalService, router: Router) {
    return () => {
        return msalService.handleRedirectObservable().pipe(
            map(() => {
                const accounts = msalService.instance.getAllAccounts();
                if (accounts.length > 0) {
                    const activeAccount = msalService.instance.getActiveAccount() || accounts[0];
                    msalService.instance.setActiveAccount(activeAccount);
                }
                return undefined;
            }),
            catchError(error => {
                console.error('Error during MSAL initialization:', error);
                void router.navigateByUrl('unauthorized');
                return of(undefined);
            }),
            take(1)
        );
    };
}

function initializeLanguage(translateService: TranslateService) {
    return () => {
        translateService.setFallbackLang('en');
        translateService.use('en');
        return of(undefined);
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(BrowserModule),
        provideTranslateService({
            loader: provideTranslateLoader(TranslationLoader),
            fallbackLang: 'en'
        }),
        SnackbarService,
        provideTheme('sag'),
        provideRouter(routes),
        provideAnimations(),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideStore(
            [
                UserManagementState,
                AccountingUnitState,
                DataUploadState,
                FleetManagementState,
                LarsExportState,
                QuarterlyExportState
            ],
            { compatibility: { strictContentSecurityPolicy: true } },
            withNgxsReduxDevtoolsPlugin()
        ),
        {
            provide: MSAL_INSTANCE,
            useFactory: msalInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: msalGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: msalInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedInterceptor,
            multi: true
        },
        provideAppInitializer(() => {
            const initializerFn = initializeMsal(inject(MsalService), inject(Router));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initializeLanguage(inject(TranslateService));
            return initializerFn();
        })
    ]
};
